'use client'

import * as React from 'react'
import { Search, FileText, CheckCircle, Clock, Download, MoreHorizontal, Calendar } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { cn } from 'lib/utils'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../firebase'

const statusConfig = {
  pending: { icon: <Clock className="size-3.5" />, color: 'bg-warning/15 text-warning' },
  verified: { icon: <CheckCircle className="size-3.5" />, color: 'bg-success/15 text-success' },
}

const typeLabels = {
  document: 'Document',
  photo: 'Photo',
  dataset: 'Dataset',
  report: 'Report',
  survey: 'Survey',
}

function formatDate(value) {
  if (!value) return '—'
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' })
}

function getEvidenceType(evidence) {
  const mime = (evidence?.type || '').toLowerCase()
  const name = (evidence?.name || '').toLowerCase()
  if (mime.startsWith('image/') || name.match(/\.(png|jpg|jpeg|gif|webp)$/)) return 'photo'
  if (mime.includes('csv') || mime.includes('spreadsheet') || mime.includes('excel') || name.endsWith('.csv')) return 'dataset'
  if (name.includes('report')) return 'report'
  if (name.includes('survey')) return 'survey'
  return 'document'
}

export default function ReportsPage() {
  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [typeFilter, setTypeFilter] = React.useState('all')
  const [reportRecords, setReportRecords] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadReports() {
      try {
        const [indicatorSnap, projectSnap] = await Promise.all([
          getDocs(collection(db, 'indicators')),
          getDocs(collection(db, 'projects')),
        ])
        if (!isMounted) return
        const indicators = indicatorSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const projects = projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const projectMap = projects.reduce((acc, project) => {
          acc[project.id] = project
          return acc
        }, {})

        const records = indicators
          .filter((indicator) => indicator.evidence?.url)
          .map((indicator) => {
            const evidence = indicator.evidence || {}
            const type = getEvidenceType(evidence)
            const project = projectMap[indicator.projectId]
            const status = evidence.verified ? 'verified' : 'pending'
            return {
              id: indicator.id,
              indicatorName: indicator.name || 'Indicator',
              reportName: evidence.name || indicator.name || 'Indicator Evidence',
              type,
              status,
              projectName: project?.code || project?.shortCode || project?.title || indicator.projectId || 'Project',
              uploadedAt: indicator.updatedAt || indicator.createdAt,
              url: evidence.url,
            }
          })
        setReportRecords(records)
      } catch {
        if (!isMounted) return
        setReportRecords([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadReports()

    return () => {
      isMounted = false
    }
  }, [])

  const filtered = reportRecords.filter((record) => {
    const matchesSearch =
      record.projectName.toLowerCase().includes(search.toLowerCase()) ||
      record.indicatorName.toLowerCase().includes(search.toLowerCase()) ||
      record.reportName.toLowerCase().includes(search.toLowerCase())
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter
    const matchesType = typeFilter === 'all' || record.type === typeFilter
    return matchesSearch && matchesStatus && matchesType
  })

  const totalReports = reportRecords.length
  const verifiedReports = reportRecords.filter((record) => record.status === 'verified').length
  const pendingReports = reportRecords.filter((record) => record.status === 'pending').length

  const compliancePie = [
    { name: 'Verified', value: verifiedReports, color: 'oklch(0.55 0.16 150)' },
    { name: 'Pending', value: pendingReports, color: 'oklch(0.87 0.15 85)' },
  ]

  const reportTypeData = Object.keys(typeLabels).map((type) => ({
    type: typeLabels[type],
    count: reportRecords.filter((record) => record.type === type).length,
  }))

  const reportsByProject = Array.from(
    reportRecords.reduce((acc, record) => {
      const current = acc.get(record.projectName) || { project: record.projectName, verified: 0, pending: 0 }
      if (record.status === 'verified') current.verified += 1
      if (record.status === 'pending') current.pending += 1
      acc.set(record.projectName, current)
      return acc
    }, new Map()),
  ).map(([, value]) => value)

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
            Reporting & Donor Compliance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Reports sourced from indicator evidence with attachments
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : totalReports}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Total Reports</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-warning">{loading ? '—' : pendingReports}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Pending Review</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-warning" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-success">{loading ? '—' : verifiedReports}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Verified</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{loading ? '—' : reportTypeData.reduce((sum, item) => sum + (item.count || 0), 0)}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Evidence Attached</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Compliance Donut */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Compliance Rate</CardTitle>
            <CardDescription className="text-xs">Verified vs pending evidence</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={compliancePie} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={3} strokeWidth={0}>
                  {compliancePie.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4">
              {compliancePie.map((entry) => (
                <div key={entry.name} className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="text-[10px] text-muted-foreground">{entry.name} ({entry.value})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Reports by Type */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Reports by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={reportTypeData} barSize={24}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="type" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Bar dataKey="count" fill="oklch(0.35 0.12 145)" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 10, fill: 'oklch(0.48 0.03 145)' }} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Reports by Project - Stacked */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Reports by Project</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={reportsByProject} barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="project" tick={{ fontSize: 9 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="submitted" stackId="a" fill="oklch(0.55 0.16 150)" name="Submitted/Approved" />
                <Bar dataKey="pending" stackId="a" fill="oklch(0.87 0.15 85)" radius={[4, 4, 0, 0]} name="Pending/Overdue" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search reports..." className="pl-10 h-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px] h-9"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="document">Document</SelectItem>
            <SelectItem value="photo">Photo</SelectItem>
            <SelectItem value="dataset">Dataset</SelectItem>
            <SelectItem value="report">Report</SelectItem>
            <SelectItem value="survey">Survey</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reports Table */}
      <Card className="border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Report</TableHead>
              <TableHead className="text-xs">Project</TableHead>
              <TableHead className="text-xs">Indicator</TableHead>
              <TableHead className="text-xs">Uploaded</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                  Loading reports...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                  No indicator evidence found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((report) => {
                const config = statusConfig[report.status]
                return (
                  <TableRow key={report.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="size-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-foreground">{report.reportName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{report.projectName}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{report.indicatorName}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="size-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{formatDate(report.uploadedAt)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn('text-[10px] gap-1', config?.color)}>
                        {config?.icon}
                        {report.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="size-7">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => report.url && window.open(report.url, '_blank')}>
                            <Download className="mr-2 size-3.5" />
                            View Evidence
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  )
}
